<template>
  <div class="text-accent-900 font-semibold text-base mb-5">Danger zone</div>
  <div class="flex justify-between">
    <div class="space-y-1">
      <Label for="plan" class="mb-0">Delete Workspace</Label>
      <p class="text-neutral-400 text-sm">Permanently delete this workspace</p>
    </div>
    <Button variant="danger" size="sm" @click="openDialog">Delete</Button>
    <AlertDialog v-model:open="open">
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Delete Workspace</AlertDialogTitle>
        </AlertDialogHeader>
        <div class="px-7 space-y-4">
          <AlertDialogDescription>
            Are you sure you want to delete the
            <strong>{{ workspace.name }}</strong> workspace?
          </AlertDialogDescription>
          <form id="delete-workspace" @submit="onSubmit">
            <fieldset class="flex flex-col gap-1">
              <Label for="name"> Workspace name </Label>
              <Input
                v-bind="nameAttrs"
                id="name"
                v-model="name"
                placeholder="Type in the name of the workspace to confirm deletion"
              />
              <span class="text-error-400">{{ errors?.name }}</span>
            </fieldset>
          </form>
        </div>
        <AlertDialogFooter>
          <AlertDialogCancel as-child>
            <Button variant="ghost" :disabled="isPending">Cancel</Button>
          </AlertDialogCancel>
          <Button
            variant="danger"
            form="delete-workspace"
            :loading="isPending"
            :disabled="isPending"
          >
            Continue
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  </div>
</template>

<script setup>
import { ref, watch, nextTick } from 'vue';
import { useRouter } from 'vue-router';
import { useForm } from 'vee-validate';
import { toTypedSchema } from '@vee-validate/zod';
import { z } from 'zod';
import { useMutation, useQueryClient } from '@tanstack/vue-query';

import requests from '@vuex/requests.js';

import { Input } from '@ui/components/input';
import { Label } from '@ui/components/label';
import { Button } from '@ui/components/button';
import { toast } from 'vue-sonner';
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@ui/components/alert-dialog';
import { useStore } from 'vuex';

const queryClient = useQueryClient();
const open = ref(false);
const props = defineProps({
  workspace: {
    type: Object,
    required: true,
  },
});

const validationSchema = toTypedSchema(
  z.object({
    name: z.literal(props.workspace.name),
  })
);

const { handleSubmit, errors, defineField, resetForm } = useForm({
  validationSchema,
});

const [name, nameAttrs] = defineField('name');

// We reset the form when the dialog is opened
// more info https://github.com/logaretm/vee-validate/discussions/3646
watch(open, async next => {
  if (next) {
    nextTick(() => {
      resetForm();
    });
  }
});

const openDialog = () => {
  open.value = true;
};

const router = useRouter();

const store = useStore();
const { isPending, mutate: deleteMutation } = useMutation({
  mutationFn: async () =>
    requests.workspaces.delete(store.getters['workspace/getId']),
  onSuccess: async data => {
    router.push({
      name: 'workspace-projects',
      params: { workspaceHash: data.hash },
    });
    store.dispatch('workspace/setWorkspace', { value: data });
    await queryClient.invalidateQueries();
    open.value = false;
  },
  onError: err => {
    console.error(err);
    if (err?.response?.data?.message) {
      toast.error(err.response.data.message);
    } else {
      toast.error('Deleting this workspace failed.');
    }
  },
});

const onSubmit = handleSubmit(() => deleteMutation());
</script>
